import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const WHATSAPP_NUMBER = "56928232048";

const MULTI_SELECT_QUESTIONS = [
  {
    id: "tipoSolucion",
    title: "1) ¿Qué tipo de solución necesitas?",
    options: ["Sitio web", "App móvil", "Sistema interno", "E-commerce", "Automatización con IA"]
  },
  {
    id: "etapaProyecto",
    title: "2) ¿En qué etapa estás?",
    options: ["Idea inicial", "Diseño en proceso", "Desarrollo iniciado", "Proyecto pausado", "Quiero rediseñar algo existente"]
  },
  {
    id: "objetivoPrincipal",
    title: "3) ¿Cuál es tu objetivo principal?",
    options: ["Vender más", "Ganar visibilidad", "Ahorrar tiempo", "Mejorar atención al cliente", "Digitalizar mi negocio"]
  },
  {
    id: "plazo",
    title: "4) ¿Para cuándo te gustaría iniciar?",
    options: ["Esta semana", "Este mes", "En 1 a 3 meses", "Aún lo estoy evaluando"]
  },
  {
    id: "presupuesto",
    title: "5) ¿Rango de presupuesto estimado?",
    options: ["Menos de $500.000 CLP", "$500.000 - $1.500.000 CLP", "$1.500.000 - $3.000.000 CLP", "Más de $3.000.000 CLP", "Prefiero asesoría primero"]
  },
  {
    id: "integraciones",
    title: "6) ¿Necesitas integraciones?",
    options: ["WhatsApp", "Pasarela de pago", "CRM", "ERP", "No por ahora"]
  },
  {
    id: "diseno",
    title: "7) Sobre diseño e identidad visual:",
    options: ["Ya tengo logo y marca", "Necesito diseño completo", "Solo ajustes visuales", "Quiero algo moderno y minimalista"]
  },
  {
    id: "contenido",
    title: "8) Sobre el contenido (textos/imágenes):",
    options: ["Ya tengo todo listo", "Tengo parte del material", "Necesito apoyo completo"]
  },
  {
    id: "mantenimiento",
    title: "9) ¿Te interesa soporte y mantenimiento?",
    options: ["Sí, mensual", "Sí, por horas", "No por ahora", "Quiero cotizar ambas opciones"]
  },
  {
    id: "contactoPreferido",
    title: "10) ¿Cómo prefieres que te contactemos?",
    options: ["WhatsApp", "Llamada", "Email", "Videollamada"]
  }
];

const WhatsAppIcon = ({ className = "h-7 w-7" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.787-1.48-1.758-1.653-2.055-.174-.298-.018-.459.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479s1.064 2.877 1.213 3.075c.149.198 2.095 3.2 5.076 4.487.709.306 1.261.489 1.692.625.711.226 1.359.194 1.87.118.571-.085 1.758-.719 2.006-1.414.248-.695.248-1.29.174-1.414-.075-.124-.273-.198-.57-.347Zm-5.421 7.618h-.004a10.02 10.02 0 0 1-5.103-1.397l-.365-.217-3.793.995 1.012-3.698-.24-.379a10.02 10.02 0 0 1-1.54-5.336C2.02 6.477 6.495 2 12.001 2 14.67 2 17.18 3.04 19.07 4.93a9.94 9.94 0 0 1 2.93 7.071c-.002 5.506-4.478 9.999-9.949 9.999Zm8.447-18.45A11.8 11.8 0 0 0 12.001 0C5.383 0 0 5.383 0 12a11.9 11.9 0 0 0 1.729 6.188L0 24l5.978-1.566A11.95 11.95 0 0 0 12 24h.005C18.622 24 24 18.617 24 12a11.9 11.9 0 0 0-3.502-8.45Z"
    />
  </svg>
);

const WhatsAppWidget = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    contactNumber: "",
    email: "",
    helpRequest: ""
  });
  const [answers, setAnswers] = useState(
    () => MULTI_SELECT_QUESTIONS.reduce((acc, question) => ({ ...acc, [question.id]: [] }), {})
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setShowPopup(true);
  }, []);

  const toggleOption = (questionId, optionValue) => {
    setAnswers((prev) => {
      const selected = prev[questionId] || [];
      const alreadySelected = selected.includes(optionValue);
      const nextSelected = alreadySelected
        ? selected.filter((value) => value !== optionValue)
        : [...selected, optionValue];

      return { ...prev, [questionId]: nextSelected };
    });
  };

  const updateFormField = (fieldName, fieldValue) => {
    setFormData((prev) => ({ ...prev, [fieldName]: fieldValue }));
  };

  const buildWhatsAppText = () => {
    const formattedSelections = MULTI_SELECT_QUESTIONS.map((question) => {
      const values = answers[question.id] || [];
      return `- ${question.title}\n  ${values.length ? values.join(", ") : "Sin selección"}`;
    }).join("\n\n");

    return [
      "Hola, soy un nuevo lead de Cynaxia.",
      "",
      "Datos de contacto:",
      `- Nombre: ${formData.fullName || "No indicado"}`,
      `- Número de contacto: ${formData.contactNumber || "No indicado"}`,
      `- Email: ${formData.email || "No indicado"}`,
      "",
      "¿Qué puedo hacer por ti?",
      formData.helpRequest || "No indicado",
      "",
      "Respuestas del mini chatbot:",
      formattedSelections
    ].join("\n");
  };

  const handleSendToWhatsApp = () => {
    if (!formData.fullName.trim() || !formData.contactNumber.trim() || !formData.email.trim()) {
      setErrorMessage("Completa nombre, número de contacto y email para continuar.");
      return;
    }

    setErrorMessage("");
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildWhatsAppText())}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col items-end gap-3">
      {showPopup && (
        <div className="w-[360px] overflow-hidden rounded-2xl border border-neutral-200 bg-[#efeae2] text-neutral-900 shadow-2xl">
          <div className="flex items-center justify-between bg-[#25D366] px-3 py-2 text-white">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                <WhatsAppIcon className="h-5 w-5" />
              </span>
              <div className="leading-tight">
                <p className="text-sm font-semibold">Naxia</p>
                <p className="text-xs text-white/90">En línea</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowPopup(false)}
              className="rounded-md p-1 text-white/90 transition hover:bg-black/10 hover:text-white"
              aria-label="Cerrar mensaje de WhatsApp"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[65vh] space-y-3 overflow-y-auto p-3">
            <div className="space-y-2">
              <div className="max-w-[95%] rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-sm shadow-sm">
                Hola, soy Naxia, tu asistente virtual.
              </div>
              <div className="max-w-[95%] rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-sm shadow-sm">
                Somos Cynaxia, empresa especializada en desarrollo de software, donde nuestra prioridad es ayudarte a hacer realidad tu sueño.
              </div>
            </div>

            <div className="rounded-2xl bg-white p-3 shadow-sm">
              <label className="mb-1 block text-xs font-semibold text-neutral-700">¿Cómo te llamas?</label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(event) => updateFormField("fullName", event.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-[#25D366]"
                placeholder="Tu nombre"
              />
            </div>

            <div className="rounded-2xl bg-white p-3 shadow-sm">
              <label className="mb-1 block text-xs font-semibold text-neutral-700">Número de contacto</label>
              <input
                type="text"
                value={formData.contactNumber}
                onChange={(event) => updateFormField("contactNumber", event.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-[#25D366]"
                placeholder="+56..."
              />
            </div>

            <div className="rounded-2xl bg-white p-3 shadow-sm">
              <label className="mb-1 block text-xs font-semibold text-neutral-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(event) => updateFormField("email", event.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-[#25D366]"
                placeholder="correo@ejemplo.com"
              />
            </div>

            <div className="rounded-2xl bg-white p-3 shadow-sm">
              <label className="mb-1 block text-xs font-semibold text-neutral-700">¿Qué puedo hacer por ti?</label>
              <textarea
                rows={3}
                value={formData.helpRequest}
                onChange={(event) => updateFormField("helpRequest", event.target.value)}
                className="w-full resize-none rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-[#25D366]"
                placeholder="Cuéntame brevemente tu necesidad"
              />
            </div>

            {MULTI_SELECT_QUESTIONS.map((question) => (
              <div key={question.id} className="rounded-2xl bg-white p-3 shadow-sm">
                <p className="mb-2 text-xs font-semibold text-neutral-700">{question.title}</p>
                <div className="flex flex-wrap gap-2">
                  {question.options.map((option) => {
                    const selected = (answers[question.id] || []).includes(option);
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => toggleOption(question.id, option)}
                        className={`rounded-full border px-3 py-1.5 text-xs transition ${
                          selected
                            ? "border-[#25D366] bg-[#25D366] text-white"
                            : "border-neutral-300 bg-white text-neutral-700 hover:border-[#25D366]"
                        }`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {errorMessage && (
              <p className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-600">{errorMessage}</p>
            )}

            <button
              type="button"
              onClick={handleSendToWhatsApp}
              className="inline-flex w-full items-center justify-center rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20bd5a]"
            >
              Enviar respuestas a WhatsApp
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowPopup((prev) => !prev)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl transition hover:scale-105 hover:bg-[#20bd5a]"
        aria-label="Abrir mini chat de WhatsApp"
      >
        <WhatsAppIcon />
      </button>
    </div>
  );
};

export default WhatsAppWidget;
